// Engagement-rule service — CEP-only. Rule CRUD and WA-template management
// both live in the AP_Customer_Engagement_Portal repo; there is no LAS
// dependency anywhere in this file.
//
// This service acts as a translation layer so the consumer page
// (EngagementRuleConfig.jsx) doesn't need to know about the underlying
// Mongo document shape:
//   - CEP rule  : { match_config: {login_number: 5}, template_name: 'foo', ... }
//   - UI shape  : { login_number: 5, service_type: null, watemplate: <uuid>,
//                   watemplate_detail: {name, template_name}, ... }
// Calls in from the page use the UI shape; we marshal it both directions.
//
// fetchEngagementRules / createEngagementRule point at the AP_Customer_
// Engagement_Portal repo's `events/` app (/api/v1/engagement-rules/) instead
// of cep/'s own /api/v4/cep/engagement-rules/ — same underlying EngagementRule
// Mongo collection, different Django app fronting it. updateEngagementRule /
// toggleEngagementRule / fetchEngagementRuleNameMap still hit cep/'s original
// endpoint (events/ has no update/detail endpoint yet).

const CEP_BASE_URL = process.env.REACT_APP_CEP_BASE_URL;
const EVENTS_BASE_URL = process.env.REACT_APP_EVENTS_BASE_URL || CEP_BASE_URL;

// Fail loudly at import time if these are unset, rather than silently
// building a relative "undefined/..." URL that the browser resolves
// against whatever page happens to be open (see incident: requests landing
// on /subscriber-wallet/undefined/api/v1/... instead of the backend).
if (!CEP_BASE_URL) {
  // eslint-disable-next-line no-console
  console.error('engagementService: REACT_APP_CEP_BASE_URL is not set — CEP calls will fail.');
}
if (!EVENTS_BASE_URL) {
  // eslint-disable-next-line no-console
  console.error('engagementService: REACT_APP_EVENTS_BASE_URL/REACT_APP_CEP_BASE_URL is not set — events/ calls will fail.');
}

const getHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// LAS-shape response wrapper kept for backwards-compat with the consumer
// (which checks res?.metadata?.api_status and reads res.msg). Lets us
// switch the backing store without churning the React page.
const lasOk = (data) => ({ metadata: { api_status: true }, msg: data });
const lasErr = (msg) => ({ metadata: { api_status: false }, msg });

// In-process cache of WaTemplate options. Filled on first need, refreshed
// whenever a template is created/edited so the rule→template join stays current.
let _templateCache = null;

const fetchTemplatesFromCep = async () => {
  const res = await fetch(
    `${EVENTS_BASE_URL}/api/v1/wa-templates/list/`,
    { headers: getHeaders() }
  );
  const json = await res.json();
  return (json?.metadata?.api_status && Array.isArray(json.msg)) ? json.msg : [];
};

const ensureTemplates = async () => {
  if (_templateCache === null) {
    _templateCache = await fetchTemplatesFromCep();
  }
  return _templateCache;
};

const invalidateTemplateCache = () => { _templateCache = null; };

// CEP rule shape (Mongo doc) → LAS-shape (what the consumer expects)
const cepRuleToLasShape = (rule, templatesByName) => {
  const mc = rule.match_config || {};
  const tmpl = templatesByName[rule.template_name] || null;
  return {
    id: rule.id,
    event_type: rule.event_type,
    login_number: typeof mc.login_number === 'number' ? mc.login_number : null,
    service_type: typeof mc.service_type === 'string' ? mc.service_type : null,
    // Rules created via a funnel category (events/'s category registry)
    // carry match_config.event_name instead of login_number/service_type.
    // We don't reverse-derive the category key from event_name — the
    // backend owns that mapping — but we surface the raw event_name +
    // conversion_event so the table/edit-form can show it.
    category_event_name: typeof mc.event_name === 'string' ? mc.event_name : null,
    conversion_event: rule.conversion_event || null,
    // Booking Step's admin-facing label ("Slot Selection Page"), resolved
    // server-side (events/api/views_engagement_rules/list.py) the same way
    // EngagementMessageLogs' journey_name is resolved. null for non-category
    // rules (login milestone / cart abandonment).
    journey_name: typeof rule.journey_name === 'string' ? rule.journey_name : null,
    delay_minutes: rule.delay_minutes,
    rate_limit_strategy: rule.rate_limit_strategy || null,
    rate_limit_window_hours: typeof rule.rate_limit_window_hours === 'number' ? rule.rate_limit_window_hours : null,
    recurring_enabled: Boolean(rule.recurring_enabled),
    max_reminders: typeof rule.max_reminders === 'number' ? rule.max_reminders : null,
    active: rule.active,
    // The consumer's form binds to watemplate (UUID). The display reads
    // watemplate_detail.{name, template_name}. We reconstruct both from
    // CEP's stored template_name string + the WaTemplate cache.
    watemplate: tmpl ? tmpl.id : null,
    watemplate_detail: tmpl
      ? { name: tmpl.name, template_name: tmpl.template_name }
      : (rule.template_name
          ? { name: rule.template_name, template_name: rule.template_name }
          : null),
  };
};

// LAS-shape payload (from the consumer) → CEP create/update body
const lasPayloadToCepShape = (payload, templatesById) => {
  // Consumer sends watemplate (UUID); CEP stores template_name (string).
  // Look up the WaTemplate row to get the EnableX template_name.
  const tmpl = payload.watemplate ? templatesById[payload.watemplate] : null;
  const template_name = tmpl?.template_name || tmpl?.name || '';

  // Funnel-category rules: hand the category key straight through — the
  // backend derives match_config/conversion_event/event_type from its own
  // registry (events/event_categories.py). No match_config built here.
  if (payload.category) {
    const body = {
      name: `${payload.category}_${template_name || 'reminder'}`,
      category: payload.category,
      template_name,
      delay_minutes: payload.delay_minutes,
    };
    if (payload.rate_limit_strategy) body.rate_limit_strategy = payload.rate_limit_strategy;
    if (payload.rate_limit_window_hours) body.rate_limit_window_hours = payload.rate_limit_window_hours;
    // recurring_enabled/max_reminders only mean anything for once_per_window
    // — the backend serializer rejects recurring_enabled=True with any
    // other strategy, so don't send it true unless that's actually selected.
    if (payload.rate_limit_strategy === 'once_per_window') {
      body.recurring_enabled = Boolean(payload.recurring_enabled);
      if (payload.recurring_enabled) body.max_reminders = payload.max_reminders;
    }
    return body;
  }

  const match_config = {};
  if (typeof payload.login_number === 'number') {
    match_config.login_number = payload.login_number;
  }
  if (payload.service_type) {
    match_config.service_type = payload.service_type;
  }

  // CEP requires a non-empty name field on EngagementRule. Synthesize a
  // stable, human-debuggable one from the rule's identity.
  const nameParts = [payload.event_type];
  if (typeof payload.login_number === 'number') nameParts.push(String(payload.login_number));
  if (payload.service_type) nameParts.push(payload.service_type);
  const name = nameParts.join('_');

  return {
    name,
    event_type: payload.event_type,
    match_config,
    template_name,
    delay_minutes: payload.delay_minutes,
  };
};

const buildTemplateMaps = async () => {
  const templates = await ensureTemplates();
  const byName = {};
  const byId = {};
  templates.forEach((t) => {
    if (t?.template_name) byName[t.template_name] = t;
    if (t?.name && !byName[t.name]) byName[t.name] = t;
    if (t?.id) byId[t.id] = t;
  });
  return { byName, byId };
};

// ─────────────────────────── public surface ───────────────────────────

// Top-level booking-flow picker (events/event_categories.py SERVICES) —
// "VC Booking Reminder" / "Diagnostics Booking Reminder".
export const fetchEngagementRuleServices = async () => {
  try {
    const res = await fetch(`${EVENTS_BASE_URL}/api/v1/engagement-rules/services/`, {
      headers: getHeaders(),
    });
    const json = await res.json();
    if (!res.ok || !json?.metadata?.api_status) {
      return lasErr(json?.msg || `fetch services failed: ${res.status}`);
    }
    return lasOk(json.msg || []);
  } catch (err) {
    return lasErr(err?.message || 'fetch services failed');
  }
};

// Funnel-stage categories (events/event_categories.py) — the dropdown
// options for "which step should this reminder trigger on", filtered to
// the selected service (VC or Diagnostics) when provided.
export const fetchEngagementRuleCategories = async (service) => {
  try {
    const url = new URL(`${EVENTS_BASE_URL}/api/v1/engagement-rules/categories/`);
    if (service) url.searchParams.set('service', service);

    const res = await fetch(url.toString(), { headers: getHeaders() });
    const json = await res.json();
    if (!res.ok || !json?.metadata?.api_status) {
      return lasErr(json?.msg || `fetch categories failed: ${res.status}`);
    }
    return lasOk(json.msg || []);
  } catch (err) {
    return lasErr(err?.message || 'fetch categories failed');
  }
};

export const fetchEngagementRules = async (eventType) => {
  try {
    const { byName } = await buildTemplateMaps();

    const url = new URL(`${EVENTS_BASE_URL}/api/v1/engagement-rules/list/`);
    if (eventType) url.searchParams.set('event_type', eventType);
    // Show both active + inactive so the toggle UI works.
    url.searchParams.set('active', 'all');

    const res = await fetch(url.toString(), { headers: getHeaders() });
    const json = await res.json();
    if (!res.ok || !json?.metadata?.api_status) {
      return lasErr(json?.msg || `fetch rules failed: ${res.status}`);
    }
    const rules = (json.msg || []).map((r) => cepRuleToLasShape(r, byName));
    return lasOk(rules);
  } catch (err) {
    return lasErr(err?.message || 'fetch failed');
  }
};

export const createEngagementRule = async (payload) => {
  try {
    const { byId, byName } = await buildTemplateMaps();
    const cepBody = lasPayloadToCepShape(payload, byId);

    const res = await fetch(`${EVENTS_BASE_URL}/api/v1/engagement-rules/create/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(cepBody),
    });
    const json = await res.json();
    if (!res.ok || !json?.metadata?.api_status) {
      return lasErr(json?.msg || 'create failed');
    }
    return lasOk(cepRuleToLasShape(json.msg, byName));
  } catch (err) {
    return lasErr(err?.message || 'create failed');
  }
};

export const updateEngagementRule = async (ruleId, payload) => {
  try {
    const { byId, byName } = await buildTemplateMaps();
    const cepBody = lasPayloadToCepShape(payload, byId);

    // Booking Step (category) rules must go through events/'s own update
    // endpoint — cep/'s PATCH has no concept of `category` and would
    // silently drop it, leaving match_config/conversion_event stale.
    const url = payload.category
      ? `${EVENTS_BASE_URL}/api/v1/engagement-rules/${ruleId}/update/`
      : `${CEP_BASE_URL}/api/v4/cep/engagement-rules/${ruleId}/`;

    const res = await fetch(url, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(cepBody),
    });
    const json = await res.json();

    if (payload.category) {
      if (!res.ok || !json?.metadata?.api_status) {
        return lasErr(json?.msg || 'update failed');
      }
      return lasOk(cepRuleToLasShape(json.msg, byName));
    }

    if (!res.ok) return lasErr(json?.detail || json || 'update failed');
    return lasOk(cepRuleToLasShape(json, byName));
  } catch (err) {
    return lasErr(err?.message || 'update failed');
  }
};

export const toggleEngagementRule = async (ruleId) => {
  // CEP has no toggle endpoint — flip via PATCH. Two round-trips (GET
  // current, then PATCH the opposite) but that's fine for an admin
  // action; volume is trivial.
  try {
    const getRes = await fetch(
      `${CEP_BASE_URL}/api/v4/cep/engagement-rules/${ruleId}/`,
      { headers: getHeaders() }
    );
    if (!getRes.ok) return lasErr(`fetch before toggle failed: ${getRes.status}`);
    const current = await getRes.json();

    const patchRes = await fetch(
      `${CEP_BASE_URL}/api/v4/cep/engagement-rules/${ruleId}/`,
      {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ active: !current.active }),
      }
    );
    const patchJson = await patchRes.json();
    if (!patchRes.ok) return lasErr(patchJson?.detail || patchJson || 'toggle failed');
    const { byName } = await buildTemplateMaps();
    return lasOk(cepRuleToLasShape(patchJson, byName));
  } catch (err) {
    return lasErr(err?.message || 'toggle failed');
  }
};

// ─────────── events/'s EngagementSent ledger (read-only) ───────────
// cep/'s message-logs endpoint only shows sends from cep/'s OWN dispatch
// pathway (cep.events.process_event → send_engagement_wa) — it stays empty
// for reminders sent via events/'s pipeline (send_reminder_if_inactive),
// since that task only writes EngagementSent, not EngagementMessageLog.
// This is the real audit trail for events/-driven reminder sends.
export const fetchEngagementSent = async ({
  user_id, engagement_rule_id, status, recurring_status, sent, event_name, search, page = 1, items = 25,
} = {}) => {
  try {
    const url = new URL(`${EVENTS_BASE_URL}/api/v1/engagement-sent/list/`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('items', String(items));
    if (user_id) url.searchParams.set('user_id', user_id);
    if (engagement_rule_id) url.searchParams.set('engagement_rule_id', engagement_rule_id);
    if (status) url.searchParams.set('status', status);
    if (recurring_status) url.searchParams.set('recurring_status', recurring_status);
    if (sent !== undefined && sent !== null && sent !== '') url.searchParams.set('sent', String(sent));
    if (event_name) url.searchParams.set('event_name', event_name);
    // Single search box — same contract as fetchApplicationEvents: the
    // backend matches this one term against application_id, mobile_no, or
    // ref_code (see events/api/views_engagement_sent/list.py).
    if (search) url.searchParams.set('search', search);

    const res = await fetch(url.toString(), { headers: getHeaders() });
    const json = await res.json();
    if (!res.ok || !json?.metadata?.api_status) {
      return lasErr(json?.msg || `fetch engagement-sent failed: ${res.status}`);
    }
    return lasOk(json.msg);
  } catch (err) {
    return lasErr(err?.message || 'fetch engagement-sent failed');
  }
};

// ─────────── Raw application events (events.documents.Event) ───────────
// Every event any application ingests via POST /api/v1/events/ — page
// views, quota checks, slot lookups, conversions, etc. Distinct from
// fetchEngagementSent above: that's CEP's per-(user, rule) reminder ledger
// ("did we send/repeat/convert this campaign?"), this is the raw firehose
// of everything applications reported happening ("what did the app say
// occurred?"), regardless of whether any engagement rule matched it.
export const fetchApplicationEvents = async ({
  event_name, search, page = 1, items = 25,
} = {}) => {
  try {
    const url = new URL(`${EVENTS_BASE_URL}/api/v1/events/list/`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('items', String(items));
    if (event_name) url.searchParams.set('event_name', event_name);
    // Single search box on the frontend — the backend matches this one
    // term against application_id, mobile_no, and ref_code together
    // (see events/api/views_events/list.py) so callers don't need to
    // guess which field the operator typed.
    if (search) url.searchParams.set('search', search);

    const res = await fetch(url.toString(), { headers: getHeaders() });
    const json = await res.json();
    if (!res.ok || !json?.metadata?.api_status) {
      return lasErr(json?.msg || `fetch application events failed: ${res.status}`);
    }
    return lasOk(json.msg);
  } catch (err) {
    return lasErr(err?.message || 'fetch application events failed');
  }
};

// ─────────── CEP engagement message logs (read-only) ───────────
// Operator-facing visibility into WhatsApp engagement sends — "did it
// send / was it skipped & why / delivered / read / converted". Read-only;
// CEP masks recipient + body_params at source (no raw PII crosses the wire).
//
// Response envelope is BARE: { count, next, previous, results: [...] } —
// the same shape fetchEngagementRules consumes. There is NO {data:...}
// wrapper (verified against the deployed CEP views; api-spec.md is stale).

const MAX_LOG_PAGE_SIZE = 100;
const DEFAULT_LOG_PAGE_SIZE = 25;

// Fetch a page of engagement message logs. All filter args optional.
//   { engagement_rule_id, status, attempted_at_after, attempted_at_before,
//     page, page_size }
// Returns lasOk({ results, count, page, page_size }) or lasErr(msg).
export const fetchMessageLogs = async ({
  engagement_rule_id,
  engagement_sent_id,
  status,
  attempted_at_after,
  attempted_at_before,
  page = 1,
  page_size = DEFAULT_LOG_PAGE_SIZE,
} = {}) => {
  try {
    // Guard pagination: the CEP view does int(param) unguarded and 500s on
    // a blank/NaN value. Coerce to safe positive integers before sending,
    // and clamp page_size to the backend's documented max.
    const safePage = Number.isInteger(page) && page > 0 ? page : 1;
    let safeSize = parseInt(page_size, 10);
    if (!Number.isInteger(safeSize) || safeSize < 1) safeSize = DEFAULT_LOG_PAGE_SIZE;
    if (safeSize > MAX_LOG_PAGE_SIZE) safeSize = MAX_LOG_PAGE_SIZE;

    const url = new URL(`${CEP_BASE_URL}/api/v4/cep/message-logs/`);
    url.searchParams.set('page', String(safePage));
    url.searchParams.set('page_size', String(safeSize));
    if (engagement_rule_id) url.searchParams.set('engagement_rule_id', engagement_rule_id);
    if (engagement_sent_id) url.searchParams.set('engagement_sent_id', engagement_sent_id);
    if (status) url.searchParams.set('status', status);
    if (attempted_at_after) url.searchParams.set('attempted_at_after', attempted_at_after);
    if (attempted_at_before) url.searchParams.set('attempted_at_before', attempted_at_before);

    const res = await fetch(url.toString(), { headers: getHeaders() });
    if (!res.ok) {
      const body = await res.text();
      return lasErr(`fetch message logs failed: ${res.status} ${body.slice(0, 200)}`);
    }
    const json = await res.json();
    return lasOk({
      results: Array.isArray(json.results) ? json.results : [],
      count: typeof json.count === 'number' ? json.count : 0,
      page: safePage,
      page_size: safeSize,
    });
  } catch (err) {
    return lasErr(err?.message || 'fetch message logs failed');
  }
};

// Build { ruleId: humanLabel } so the log table can render rule names
// instead of raw UUIDs (the log list returns engagement_rule_id only).
// CEP rules carry a `name`; fall back to a composed event+match label,
// then to the short id. Returns lasOk(map) or lasErr(msg).
export const fetchEngagementRuleNameMap = async () => {
  try {
    const url = new URL(`${CEP_BASE_URL}/api/v4/cep/engagement-rules/`);
    url.searchParams.set('active', 'all');
    // Pull a large page so the name map covers all rules — the list view
    // defaults to 25, which would leave rules >25 showing raw ids in the
    // log table + missing from the filter dropdown. Engagement rule counts
    // are small (a handful per event type); 100 is ample headroom.
    url.searchParams.set('page_size', '100');

    const res = await fetch(url.toString(), { headers: getHeaders() });
    if (!res.ok) {
      const body = await res.text();
      return lasErr(`fetch rule name map failed: ${res.status} ${body.slice(0, 200)}`);
    }
    const json = await res.json();
    const map = {};
    (json.results || []).forEach((r) => {
      if (!r?.id) return;
      const composed = [
        r.event_type,
        r.match_config ? JSON.stringify(r.match_config) : null,
      ].filter(Boolean).join(' ');
      map[r.id] = r.name || composed || String(r.id).slice(0, 8);
    });
    return lasOk(map);
  } catch (err) {
    return lasErr(err?.message || 'fetch rule name map failed');
  }
};

// ─────────── WA-template management — owned by CEP ───────────
// WaTemplate (cep.documents.WaTemplate) lives in CEP's own Mongo, not LAS.
// There is no EnableX-sync step — templates + their variable mappings are
// created and edited here directly. Variable values are resolved at send
// time from the triggering Event's own data (event_data / event_user) —
// see cep/services/wa_template.py.

export const createWaTemplate = async (payload) => {
  try {
    const response = await fetch(`${EVENTS_BASE_URL}/api/v1/wa-templates/create/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await response.json();
    invalidateTemplateCache();
    if (!response.ok || !json?.metadata?.api_status) {
      return lasErr(json?.msg || 'create template failed');
    }
    return lasOk(json.msg);
  } catch (err) {
    return lasErr(err?.message || 'create template failed');
  }
};

export const updateTemplateVariables = async (templateId, variables) => {
  try {
    const response = await fetch(
      `${EVENTS_BASE_URL}/api/v1/wa-templates/${templateId}/variables/`,
      {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ variables }),
      }
    );
    const json = await response.json();
    // Variables changed — drop cache so rule joins re-fetch fresh template data.
    invalidateTemplateCache();
    return json;
  } catch (err) {
    return lasErr(err?.message || 'update variables failed');
  }
};

export const fetchWaTemplateOptions = async () => {
  try {
    const response = await fetch(`${EVENTS_BASE_URL}/api/v1/wa-templates/list/`, {
      headers: getHeaders(),
    });
    const json = await response.json();
    // Seed the internal cache from this call (the consumer fetches templates
    // alongside rules on page mount; reuse that response to avoid a duplicate
    // fetch on the next rule list/create/update).
    if (json?.metadata?.api_status && Array.isArray(json.msg)) {
      _templateCache = json.msg;
    }
    return json;
  } catch (err) {
    return lasErr(err?.message || 'fetch templates failed');
  }
};

// ─────────── Event catalog — owned by CEP ───────────
// cep.documents.EventCatalog is the registry of known trigger/conversion
// event names. Booking steps reference these instead of hand-typed strings,
// so this is what populates the "Trigger Event" / "Target Event" dropdowns
// on the "Create New Booking Step" form.

// `service` filters the dropdown down to events tagged for that service
// (VC/Diagnostics/Loan/Medicine) — omit to fetch the full catalog.
export const fetchEventCatalog = async (service) => {
  try {
    const url = new URL(`${EVENTS_BASE_URL}/api/v1/event-catalog/list/`);
    if (service) url.searchParams.set('service', service);

    const res = await fetch(url.toString(), { headers: getHeaders() });
    const json = await res.json();
    if (!res.ok || !json?.metadata?.api_status) {
      return lasErr(json?.msg || `fetch event catalog failed: ${res.status}`);
    }
    return lasOk(json.msg || []);
  } catch (err) {
    return lasErr(err?.message || 'fetch event catalog failed');
  }
};

export const createEventCatalogEntry = async (eventName, service) => {
  try {
    const res = await fetch(`${EVENTS_BASE_URL}/api/v1/event-catalog/create/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ event_name: eventName, service }),
    });
    const json = await res.json();
    if (!res.ok || !json?.metadata?.api_status) {
      return lasErr(json?.msg || 'create event failed');
    }
    return lasOk(json.msg);
  } catch (err) {
    return lasErr(err?.message || 'create event failed');
  }
};

// ─────────── Booking steps — owned by CEP ───────────
// cep.documents.BookingStep is the DB-backed replacement for the old
// hardcoded events/event_categories.py dict — admins create these from the
// "+ Create New Booking Step" UI instead of a developer editing Python and
// redeploying. fetchEngagementRuleCategories() (above) already reads from
// this same table via the legacy /engagement-rules/categories/ endpoint —
// these two new functions are for the create-new-step flow specifically.

export const createBookingStep = async ({ service, label, triggerEventId, targetEventId }) => {
  try {
    const res = await fetch(`${EVENTS_BASE_URL}/api/v1/booking-steps/create/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        service,
        label,
        trigger_event: triggerEventId,
        target_event: targetEventId,
      }),
    });
    const json = await res.json();
    if (!res.ok || !json?.metadata?.api_status) {
      return lasErr(json?.msg || 'create booking step failed');
    }
    return lasOk(json.msg);
  } catch (err) {
    return lasErr(err?.message || 'create booking step failed');
  }
};
